import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Link2 } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StatusBadge } from "@/domains/pm/components/shared";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { cn } from "@/lib/utils";

// WHA-3270 — single chip style for both 1-issue and N-issue variants.
// Outlined (1px border, transparent fill) so the chip reads as a quiet
// affordance next to the noisier filled category/version chips on the
// same row. Brand-primary text + icon tint on hover so it still feels
// PM-attached when the user notices it.
const CHIP_CLASSES =
  "inline-flex h-6 shrink-0 items-center gap-1 rounded-md border border-border bg-background px-1.5 text-[10px] font-medium text-muted-foreground transition-colors hover:border-primary/40 hover:bg-primary/5 hover:text-primary";

export interface LinkedIssueRef {
  id: number;
  issue_number: number;
  title: string;
  building_id: number;
  status: "in_progress" | "resolved" | "closed";
}

interface LinkedIssuesChipProps {
  issues: LinkedIssueRef[];
  /** Optional className passthrough so callers can tweak vertical alignment
   *  inside their row toolbar. */
  className?: string;
}

/**
 * WHA-3270 — small chip rendered next to the preview icon on every
 * document row that's been attached to one or more PM issues.
 *
 *   • 0 issues  → renders nothing.
 *   • 1 issue   → `#N` button; tooltip names the issue title; click
 *                  navigates to `/buildings/{building_id}/issues?selected={id}`.
 *   • N issues  → `+N` button; click opens a modal listing all issues,
 *                  each click navigates and closes.
 *
 * The modal uses the same cmdk pattern as ParentIssuePickerDialog so the
 * search affordance stays consistent across PM dialogs.
 */
export function LinkedIssuesChip({ issues, className }: LinkedIssuesChipProps) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  if (issues.length === 0) return null;

  const navigateToIssue = (issue: LinkedIssueRef) => {
    navigate(`/buildings/${issue.building_id}/issues?selected=${issue.id}`);
  };

  if (issues.length === 1) {
    const only = issues[0];
    const tooltip = t.pm.issues.detail.linkedToIssue
      .replace("{n}", String(only.issue_number))
      .replace("{title}", only.title);
    return (
      <button
        type="button"
        title={tooltip}
        onClick={(e) => {
          e.stopPropagation();
          navigateToIssue(only);
        }}
        className={cn(CHIP_CLASSES, className)}
        aria-label={tooltip}
      >
        <Link2 className="h-3 w-3" aria-hidden />
        <span>#{only.issue_number}</span>
      </button>
    );
  }

  const summary = t.pm.issues.detail.linkedToIssues.replace("{count}", String(issues.length));

  return (
    <>
      <button
        type="button"
        title={summary}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        className={cn(CHIP_CLASSES, className)}
        aria-label={summary}
      >
        <Link2 className="h-3 w-3" aria-hidden />
        <span>+{issues.length}</span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg overflow-hidden p-0">
          <DialogHeader className="px-4 pb-2 pt-4">
            <DialogTitle className="text-base">{summary}</DialogTitle>
          </DialogHeader>
          <Command shouldFilter>
            <CommandInput placeholder={t.pm.issues.detail.searchIssues} />
            <CommandList>
              <CommandEmpty>{t.pm.issues.empty}</CommandEmpty>
              <CommandGroup>
                {issues.map((issue) => (
                  <CommandItem
                    key={issue.id}
                    value={`#${issue.issue_number} ${issue.title}`}
                    onSelect={() => {
                      navigateToIssue(issue);
                      setOpen(false);
                    }}
                    className="flex items-center gap-3 px-3 py-2"
                  >
                    <span className="text-[11px] font-medium text-muted-foreground">
                      #{issue.issue_number}
                    </span>
                    <span className="flex-1 truncate text-sm">{issue.title}</span>
                    <StatusBadge status={issue.status} />
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </DialogContent>
      </Dialog>
    </>
  );
}
